import { type ReactNode } from 'react'

import type { OnlyFansMessage } from '@api'
import { useUnit } from 'effector-react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { IconLoading } from '@/assets/icons/icon-loading'
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { $activeChatDetails, $fanId } from '@/models/chattingUI/chats/chats.ts'
import { sendMessageFx } from '@/models/chattingUI/messages/messages.ts'
import MoreIcon from '@/modules/chatting/icons/more.svg'
import { classNames } from '@/utils/helpers.ts'

import type { MessageProps } from './ChatMessage'
import { ChatMessageMedias } from './ChatMessageMedias'
import { ChatMessageReleaseForms } from './ChatMessageReleaseForms'
import { ChatMessageStory } from './ChatMessageStory'
import { ChatMessageText } from './ChatMessageText'
import { MessageReplyPreview } from './MessageReplyPreview'
import { PriceTag } from './PriceTag'

interface Props extends MessageProps {
  messageId: string
  belongsToPrevMessage: boolean
  onViewMessageClick: (m?: OnlyFansMessage) => void
  dropdownContent: ReactNode
  messageTag?: ReactNode
  isTempMessage?: boolean
}

export const ChatMessageFromUser = ({
  message,
  hasMessage,
  belongsToPrevMessage,
  messageId,
  onViewMessageClick,
  dropdownContent,
  messageTag,
  searchedKeyword,
  isTempMessage = false,
}: Props) => {
  const isResending = useUnit(sendMessageFx.pending)
  const fanId = useUnit($fanId)
  const activeChat = useUnit($activeChatDetails)
  const { media, releaseForms, replyToMessage, isDeleted, story } = message
  const hasError = message._hasError

  const handleRetry = async () => {
    await sendMessageFx({
      userId: fanId!,
      message,
      chat: activeChat,
      optimisticMessageId: (Math.random() * 10000000) >> 0,
    })
  }

  return (
    <div
      id={messageId}
      className={classNames({
        'flex w-full flex-col items-end': true,
        'mt-4': !belongsToPrevMessage,
        'mt-1': belongsToPrevMessage,
      })}
      key={messageId}
    >
      <div
        className={classNames('max-w-[calc(60%+28px)]', {
          'mb-4': !message.isFree,
        })}
      >
        {story && (
          <ChatMessageStory
            story={story}
            className="flex flex-col items-end"
          />
        )}
        <div className="relative flex w-full justify-end">
          {!isDeleted && !hasError && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="mr-2 h-10"
                disabled={isTempMessage}
              >
                {isTempMessage ? (
                  <IconLoading className="h-4 w-4 animate-spin" />
                ) : (
                  <MoreIcon className="text-secondary-300 hover:text-primary-500" />
                )}
              </DropdownMenuTrigger>
              {dropdownContent}
            </DropdownMenu>
          )}
          <div
            className={classNames(
              'break-word bg-primary-100 dark:bg-primary-900 max-w-full break-words rounded-bl-[8px] rounded-br-[8px] rounded-tl-[8px] px-3 py-2.5',
              {
                'bg-red-200 dark:bg-red-900': message.isDeleted,
                'pb-5': !message.isFree,
              },
            )}
          >
            <MessageReplyPreview
              onViewMessageClick={onViewMessageClick}
              message={replyToMessage}
              canView={hasMessage(replyToMessage?.id ?? 0)}
            />
            {(!!media?.length || message.giphyId) && (
              <ChatMessageMedias
                message={message}
                medias={media}
              />
            )}
            <ChatMessageText
              message={message}
              searchedKeyword={searchedKeyword}
            />
            {hasError && (
              <div className="border-danger-default mt-2 flex w-full gap-1 border-t pt-2">
                <InfoIcon className="text-danger-default h-4 w-4" />
                <span className="text-xs font-medium italic">
                  Failed to send message
                </span>
                <button
                  disabled={isResending}
                  onClick={handleRetry}
                  className={classNames(
                    'text-xs font-medium italic text-blue-600',
                    { 'opacity-50 pointer-events-none': isResending },
                  )}
                >
                  Retry
                </button>
              </div>
            )}
            {message.isDeleted && (
              <div className="border-danger-default mt-2 flex w-full gap-1 border-t pt-2">
                <InfoIcon className="text-danger-default h-4 w-4" />
                <span className="text-xs font-medium italic">
                  {message.deletedByUserName
                    ? `deleted by ${message.deletedByUserName}`
                    : 'deleted'}
                </span>
              </div>
            )}
            {!!releaseForms?.length && (
              <ChatMessageReleaseForms message={message} />
            )}
          </div>
          {!message.isFree && (
            <div className="absolute -bottom-[17px] right-0">
              <PriceTag message={message} />
            </div>
          )}
        </div>
      </div>
      {messageTag}
    </div>
  )
}
